<template>
  <div class="offers">
    <div v-if="getOfferErrorMessage">
      <div v-if="shouldWaitForCx">
        <CxWaitValidation />
      </div>
      <div v-else>
        <NoOffers
          :message="getOfferErrorMessage"
          :error-code="getOfferErrorCode"
        />
      </div>
    </div>
    <div v-else>
      <Alert
        v-if="shouldShowWarningAlert"
        class="m-1 mb-2"
        :alertIcon="'fa fa-info-circle'"
        :message="isRentalFlow ? t('RENTAL_INITIAL_PAYMENT_REQUIRED_STRING') : t('INITIAL_PAYMENT_REQUIRED_STRING')"
        @onClose="showWarningAlert = false"
        :isSelfService="isSelfOnboarding"
      />
      <Alert
        class="m-1 mb-2"
        v-if="errorMessage"
        :message="errorMessage"
        alertType="alert-danger"
        :closable="true"
        @onClose="errorMessage = ''"
        :isSelfService="isSelfOnboarding"
      />

      <Alert
        v-if="isOffersWithDiscount"
        class="m-1 mb-2 top-banner-info"
        :message="notifyPromotionalDiscount"
        alertType="alert-success"
        alertIcon="fa fa-info-circle"
        :closable="false"
        :isSelfService="isSelfOnboarding"
      />

      <Alert
        v-if="showOBRDeviceMessage"
        class="m-1 mb-2"
        :message="obrDeviceMessage"
        alertType="alert-success"
        alertIcon="fa fa-info-circle"
        :closable="false"
        data-testid="obr-device-approval-message"
        :isSelfService="isSelfOnboarding"
      />
      <div v-if="!loadingOffers && isSelfOnboarding">
        <div v-if="showPreOffers">
          <SelfServicePreOffers @show-survey="handleShowSurvey" />
        </div>

        <Survey
          v-if="showSurvey"
          @survey-device-chosen="surveyDeviceChosen"
          @hide-survey="completeSurvey"
        />
      </div>
      <div class="approved-div" v-if="showApprovedBadge && !isSelfOnboarding">
        <h3>
          <i class="fa fa-check-circle pj-color-green px-0"></i>
          {{ t("CONGRATULATIONS_APPROVED_FOR_OFFERS") + " 🎉" }}
        </h3>
      </div>
      <LoadingScreen
        :main-message="t('WAIT_OFFERS_LOADING')"
        :secondary-message="t('WAIT_OFFERS_LOADING_CONTEXT')"
        :progress="loadingProgress"
        v-show="loadingOffers"
      />

      <div class="m-1">
        <div class="row">
          <div v-show="onboardingAwareNotLoading" class="col-md-6">
            <RecommendedOffers
              v-if="!isSelfOnboarding"
              @recommended-offer="emitRecommendedOffer"
              :selectedRecommendedOffer="selectedDeviceModelId"
            ></RecommendedOffers>
            <Dropdown
              :dropdown-Label="manufacturerLabel"
              :options="manufacturerDropdown"
              :defaultValue="selectedManufacturer"
              v-model="selectedManufacturerM"
              @update:modelValue="setSelectedManufacturerDropdown"
              :key="componentKeyManufacturer"
              data-testid="device-manufacturer"
              :class-name="dropdownLabelClass"
              placeholder="--"
              :isSelfService="isSelfOnboarding"
            />
            <Dropdown
              v-if="showDeviceFamily"
              :dropdown-Label="deviceFamilyLabel"
              :options="deviceFamiliesDropdown"
              :defaultValue="selectedDeviceFamily"
              v-model="selectedDeviceFamilyM"
              @update:modelValue="setSelectedDeviceFamilyDropdown"
              :key="componentKeyBrand"
              data-testid="device-brand"
              :class-name="dropdownLabelClass"
              placeholder="--"
              :isSelfService="isSelfOnboarding"
            />
            <Dropdown
              v-if="showDeviceModel"
              :dropdown-Label="deviceModelLabel"
              :options="deviceModelsDropdown"
              :defaultValue="selectedDeviceModel"
              v-model="selectedDeviceModelM"
              @update:modelValue="setSelectedDeviceModelDropdown"
              :key="componentKeyModel"
              data-testid="device-model"
              :class-name="dropdownLabelClass"
              placeholder="--"
              :isSelfService="isSelfOnboarding"
            />

            <Transition name="list">
              <DevicePreview
                v-if="selectedImageUrl"
                :imageUrl="selectedImageUrl"
                :deviceName="deviceModelName"
              />
            </Transition>

            <Transition name="list">
              <PayJoyCardBanner
                v-if="showPayjoyCardBanner"
                :creditLineLimit="creditLineLimit"
              />
            </Transition>

            <Spinner v-if="showPriceInputLoader && !isSelfOnboarding" />

            <Transition name="list">
              <CartAccessories
                v-if="showCartAccessories"
                :device-price="deviceRetailPrice"
                @accessory-update="accessoryUpdated"
              />
            </Transition>

            <Transition name="list">
              <InputFieldPrice
                v-if="deviceModelId"
                :label-name="t('PRICE_INCLUDING_VAT')"
                ref="retailPriceField"
                :disabled="disablePricing"
                @price-emit="emitPrice"
                :inputValue="deviceRetailPriceInput"
                :currency="currency"
                :visible="showButton"
                data-testid="retail-price"
                :isSelfService="isSelfOnboarding"
              />
            </Transition>
            <Transition name="list">
              <InputFieldPrice
                v-if="showDownPaymentField"
                :label-name="downPaymentLabel"
                ref="downPaymentField"
                :disabled="disableDownPayment"
                @price-emit="emitDownPayment"
                :inputValue="deviceDownPayment"
                :currency="currency"
                :visible="showButton"
                data-testid="down-payment"
                :isSelfService="isSelfOnboarding"
              />
            </Transition>
            <div
              v-if="specialDownPaymentMessage && !isSelfOnboarding"
              :class="
                specialDownPaymentEligible
                  ? 'down-payment-special-promotion-eligible'
                  : ''
              "
            >
              <img
                v-if="specialDownPaymentEligible"
                src="@/apps/merchant3/assets/pricing/label-offer.svg"
                alt="Special offer label"
              />
              <span>{{ specialDownPaymentMessage }}</span>
            </div>

            <FeesAndTaxesV2
              v-if="showFeesAndTaxesV2 || displayFeesAndTaxes"
              :is-self-onboarding="isSelfOnboarding"
            />
            <div v-if="isPromotionalDiscountApplied" class="mt-3 promo-banner">
              <Alert
                :message="notifyPromotionalDiscountSelected"
                alertType="alert-success"
                alertIcon="fa fa-check"
                :closable="false"
                :isSelfService="isSelfOnboarding"
              />
            </div>
          </div>
          <div class="col-md-6" v-if="loadingOffers && !isSelfOnboarding">
            <div class="col">
              <Alert
                v-if="loadingOffers"
                :message="t('WAIT_A_MOMENT')"
                alertType="alert-warning"
              />
              <Spinner />
            </div>
          </div>
          <div class="col-md-6">
            <div class="row" v-if="pricingLoader && !isSelfOnboarding">
              <div class="col">
                <Spinner />
              </div>
            </div>
            <div class="mt-3" v-if="displayPaymentTerms">
              <div class="row">
                <div class="col">
                  <h3 class="payment-terms">
                    {{
                      isSelfOnboarding
                        ? t("SELF_ONBOARDING.PAYMENT_TERMS")
                        : t("PAYMENT_TERMS")
                    }}
                  </h3>
                </div>
              </div>
              <Transition name="list">
                <PaymentTerms
                  :class="{ 'fs-4': isSelfOnboarding }"
                  @onSelect="setSelectedOffer"
                  :disable-offer-button="disableOfferButton"
                  :selected-offer="selectedOffer"
                  :preApprovalState="preApprovalState"
                />
              </Transition>
            </div>
          </div>
        </div>
        <div class="row mt-3" v-show="readyToContinue">
          <div class="col-md-6 py-2">
            <ButtonContinue
              class="btn-next"
              :isEnabled="buttonContinue"
              :label="t('CONTINUE')"
              @callback="postSelectOffers"
            />
          </div>
        </div>

        <div>
          <PreviousStepButton />
        </div>
      </div>
    </div>
    <JointLoanExistingLineModal
      v-if="showJointLoanModal"
      @close-modal="showJointLoanModal = false"
    />
  </div>
  <HelpMessage />
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  onMounted,
  ref,
  watch,
  Ref,
  watchEffect,
  onBeforeUnmount,
  ComputedRef,
  getCurrentInstance,
} from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import PaymentTerms from "@/apps/merchant3/components/offers/PaymentTerms.vue";
import FeesAndTaxesV2 from "@/apps/merchant3/components/offers/FeesAndTaxesV2.vue";
import DevicePreview from "@/apps/merchant3/components/offers/DevicePreview.vue";
import RecommendedOffers from "@/apps/merchant3/components/offers/RecommendedOffers.vue";
import InputFieldPrice from "@/apps/merchant3/shared-components/input-fields/InputFieldPrice.vue";
import { IOptionDropdown } from "@/types/dropdown";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import {
  getPricing,
  updateDeviceModel,
  putOffers,
  setDownPayment,
  setPrice,
  getNextRoute,
} from "@/apps/merchant3/services/carts.service";
import { createBankProposal } from "@/apps/merchant3/services/bank_service";
import { AxiosError, AxiosResponse } from "axios";
import { useRouter } from "vue-router";
import useUpdatePageState from "@/apps/merchant3/hooks/useUpdateCurrentPageState";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import NoOffers from "@/apps/merchant3/shared-components/common/NoOffers.vue";
import useCurrency from "@/apps/merchant3/shared-hooks/useCurrency";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";

import { getWelcomeCallStatus } from "@/apps/merchant3/services/contract.service";
import scrollToBottom from "@/apps/merchant3/shared-helpers/scrollToBottom";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { openChatAfterSeconds } from "@/apps/merchant3/helpers/chatBotPopup";
import {
  IBadgeExperiments,
  IPaymentTerms,
} from "@/apps/merchant3/models/IStateCart";

import { GreetingKey } from "@/apps/merchant3/types/ChatBotGreetingKeys";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import CxWaitValidation from "@/apps/merchant3/components/CxWaitValidation.vue";
import IWelcomeCall from "@/apps/merchant3/types/IWelcomeCall";
import formatPrice from "@/apps/merchant3/shared-helpers/price-formatter.helper";
import { ApprovalState } from "@/apps/merchant3/types/OBR";
import useIntervalCustomerState from "@/apps/merchant3/hooks/useIntervalCustomerState";
import HelpMessage from "@/apps/merchant3/components/HelpMessage.vue";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import PreviousStepButton from "@/apps/merchant3/shared-components/ui/PreviousStepButton.vue";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import LoadingScreen from "@/apps/merchant3/components/LoadingScreen.vue";
import {
  isBrazilSaleFlow,
  isColombiaSaleFlow,
  isPeruSaleFlow,
  isRentalFlow,
  isSubscriptionFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";
import CartAccessories from "@/apps/merchant3/components/offers/CartAccessories.vue";
import PayJoyCardBanner from "@/apps/merchant3/components/offers/PayJoyCardBanner.vue";
import JointLoanExistingLineModal from "@/apps/merchant3/components/offers/JointLoanExistingLineModal.vue";
import {
  ICartAccessory,
  ICartAccessoryType,
} from "@/apps/merchant3/types/CartAccessories";
import Survey from "@/apps/merchant3/components/self-onboarding/survey/Survey.vue";
import SelfServicePreOffers from "@/apps/merchant3/components/self-onboarding/offers/SelfServicePreOffers.vue";
import {
  logDownpaymentReductionMessage,
  logPostSelectOffers,
  logPricingSetDevice,
  logPricingSetDownPayment,
  logPricingSetFamily,
  logPricingSetManufacturer,
  logPricingSetRetailPrice,
  logPricingValidPayment,
  logSendLatestCart,
} from "@/apps/merchant3/helpers/offers/loggers.helper";
import { datadogRum } from "@datadog/browser-rum";
import { formatCurrencyFromMerchant } from "@/apps/merchant3/shared-helpers/format-currency.helper";
import { EVENTS, recorder } from "@/apps/merchant3/shared-services/recording.service";
import { merchantHasUseAddOnServicesFlag } from "@/apps/merchant3/helpers/useAddOnServicesFlag";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import useFeatureFlag from "@/apps/merchant3/hooks/useFeatureFlag";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

const { t } = useI18n();
const store = useStore();
const { getCurrency } = useCurrency();
const app = getCurrentInstance()?.appContext.app;

onBeforeMount(() => {
  store.state.offers = {
    deviceFamilies: {},
  };
});

const showButton = ref(false);
const selectedDeviceModelId = ref();
const showPriceInputLoader = ref(false);
const showPayjoyCardBanner = ref(false);
const creditLineLimit = ref(0);
const showJointLoanModal = ref(false);

const isCartAccessoryBundleEnabled = computed(() => {
  const { isFeatureFlagSet } = useFeatureFlag();
  const merchantEnabled = store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.CART_ACCESSORY_BUNDLE
  );

  return (
    isFeatureFlagSet(
      AvailableFeatureFlags.CART_ACCESSORY_BUNDLE,
      merchantEnabled
    ) && merchantEnabled
  );
});
const showCartAccessories = computed(() => {
  return isCartAccessoryBundleEnabled.value && deviceModelId.value;
});

const jointModalShownKey = "jointLoanExistingLineModalShown";

const cartAccessoryPrice = ref();
const cartAccessoryType = ref();
const cartAccessoryIsRemoved = ref();
const accessoryUpdated = (newCartAccessory: ICartAccessory) => {
  cartAccessoryPrice.value = newCartAccessory.price;
  cartAccessoryType.value = newCartAccessory.type;
  cartAccessoryIsRemoved.value = newCartAccessory.isRemoved;

  if (!deviceRetailPrice.value) {
    return;
  }

  const payload = {
    cartTag: store.state.carts.cartTag,
    retailPrice: cartRetailPrice.value,
    accessoryPrice: newCartAccessory.price,
    accessoryType: newCartAccessory.type,
    accessoryIsRemoved: newCartAccessory.isRemoved,
  };
  updateRetailPrice(payload);
};

const nextRouteIsSelfOnboarding = ref();
const showApprovedBadge = computed(() => {
  return !!store.state.offers?.manufacturerDropdown;
});

const preApprovalState: Ref<ApprovalState> = ref(ApprovalState.PENDING);
const showOBRDeviceMessage = computed(() => {
  return (
    preApprovalState.value === ApprovalState.DISAPPROVED ||
    preApprovalState.value === ApprovalState.APPROVED
  );
});

const obrDeviceMessage = computed(() => {
  if (preApprovalState.value === ApprovalState.APPROVED) {
    return t("OBR.DEVICE_QUALIFIED");
  }

  if (preApprovalState.value === ApprovalState.DISAPPROVED) {
    return t("OBR.OTHER_QUALIFIED_DEVICES");
  }
  return "";
});

const readyToContinue: ComputedRef<boolean> = computed(() => {
  return (
    (!isSelfOnboarding.value && !loadingOffers.value) ||
    (isSelfOnboarding.value && surveyIsComplete.value)
  );
});

const modelIdMatched = ref(false);

const notifyPromotionalDiscount = ref(
  t("PROMOTIONAL_DP_DISCOUNT_NOTIFY_MESSAGE")
);

const notifyPromotionalDiscountSelected = ref("");

const cartTag = computed(() => {
  return store.state.carts.cartTag;
});

const selectedDeviceFamilyM = ref();
const selectedDeviceModelM = ref();
const selectedManufacturerM = ref();

const specialDownPaymentEligible = ref(false);
const specialDownPaymentMessage = ref("");

watchEffect(() => {
  if (
    store.getters["carts/hasActiveFeature"](
      CartActiveFeatures.CHECKOUT_ON_EXISTING_CREDIT_LINE
    ) &&
    store.state.offers.selectedDeviceModelObject?.minDownPayment != undefined
  ) {
    const retailPrice10Percent = store.state.carts.priceWithSalesTax / 10;

    if (
      store.state.offers.selectedDeviceModelObject.minDownPayment <=
      retailPrice10Percent
    ) {
      specialDownPaymentMessage.value = t("CREDIT_LINE_SPECIAL_DOWN_PAYMENT");
      specialDownPaymentEligible.value = true;
      return;
    } else {
      specialDownPaymentMessage.value = t(
        "CREDIT_LINE_NO_SPECIAL_DOWN_PAYMENT"
      );
      specialDownPaymentEligible.value = false;
      return;
    }
  }

  specialDownPaymentMessage.value = "";
  specialDownPaymentEligible.value = false;
});

const selectedManufacturer = computed(() => {
  return store.state.offers.selectedManufacturer;
});

const manufacturerLabel = computed(() => {
  return isSelfOnboarding.value
    ? t("SELF_ONBOARDING.MANUFACTURER")
    : t("MANUFACTURER");
});

const manufacturerDropdown = computed(() => {
  return store.state.offers.manufacturerDropdown || [];
});

const showDeviceFamily = computed(() => {
  return (
    selectedManufacturer.value &&
    Object.keys(selectedManufacturer.value).length !== 0
  );
});

const deviceFamilyLabel = computed(() => {
  return isSelfOnboarding.value
    ? t("SELF_ONBOARDING.DEVICE_BRAND")
    : t("DEVICE_BRAND");
});

const deviceFamiliesDropdown = computed(() => {
  if (store.state.offers?.deviceFamiliesDropdown) {
    logEvent(EventLogs.Events.NUMBER_OF_DEVICE_FAMILIES_OFFERED, {
      numberFamiliesLoaded: store.state.offers?.deviceFamiliesDropdown?.length,
    });
  }
  return store.state.offers?.deviceFamiliesDropdown || [];
});
const selectedDeviceFamily = computed(() => {
  return store.state.offers.selectedDeviceFamily;
});

const showDeviceModel = computed(() => {
  return (
    selectedDeviceFamily.value &&
    Object.keys(selectedDeviceFamily.value).length !== 0
  );
});

const deviceModelLabel = computed(() => {
  return isSelfOnboarding.value
    ? t("SELF_ONBOARDING.DEVICE_MODEL")
    : t("DEVICE_MODEL");
});

const deviceModelsDropdown = computed(() => {
  if (store.state.offers?.deviceModelsDropdown) {
    logEvent(EventLogs.Events.NUMBER_OF_DEVICE_MODELS_OFFERED, {
      numberModelsLoaded: store.state.offers?.deviceModelsDropdown?.length,
    });
  }
  return store.state.offers?.deviceModelsDropdown || [];
});

const selectedDeviceModel = computed(() => {
  return store.state.offers.selectedDeviceModel;
});
const getOfferErrorMessage = computed(() => {
  return store.state.offers.offerErrorMessage;
});

const getOfferErrorCode = computed(() => {
  return store.state.offers.errorCode;
});

const shouldWaitForCx = computed(() => {
  return store.state.offers?.shouldWaitForCx;
});

const isQuickApprovalFlow = computed(
  () => store.getters["merchant/usesQuickApprovalFlow"]
);

const idAnnotations = computed(() => store.state.customer.idAnnotations);

const showDownPaymentField = computed(() => {
  return !!(deviceModelId.value && selectedImageUrl.value);
});

const downPaymentLabel = computed(() => {
  if (isSelfOnboarding.value) {
    return t("SELF_ONBOARDING.DOWN_PAYMENT");
  }
  if (feeSchema.value) {
    return t("INITIAL_PAYMENT");
  }
  if (isRentalFlow.value) {
    return t("RENTAL_DOWN_PAYMENT");
  }
  if (isSubscriptionFlow.value) {
    return t("SUBSCRIPTION_ACTIVATION_COST");
  }
  return t("DOWN_PAYMENT");
});

const showWarningAlert = ref(true);
const componentKeyManufacturer = ref(0);
const componentKeyBrand = ref(0);
const componentKeyModel = ref(0);
const displayPaymentTerms = ref(false);
const selectedOffer = ref(0);
const buttonContinue = ref(false);
const pricingLoader = ref(false);
const deviceModelFinanceLoaded = ref(false);
const errorMessage = ref("");
const disableOfferButton = ref(false);
const minDownPayment = ref();
const minDownPaymentUndiscounted = ref();
const promotionalDpExperiement = computed(() => {
  return store.state.carts?.experiments?.promoDPExperiment;
});
const isOffersWithDiscount = computed(() => {
  return (
    promotionalDpExperiement.value && store.state.offers?.offersWithDiscount
  );
});
const isPromotionalDiscountApplied = ref(false);
const promotionalDiscount = ref();
const maxDiscountEligible = ref();
const currency = computed(() => {
  return getCurrency(store.state.merchant?.merchant?.country);
});

const loadingOffers = computed(() => {
  const loading = store.getters["offers/getLoading"];

  return loading === undefined ? true : loading;
});

const selectedImageUrl = computed(() => {
  return store.state.offers.selectedDeviceImageUrl;
});
const availableOffers = computed(() => {
  return store.state.carts.pricing;
});
const deviceModelId = computed(() => {
  return store.state.offers.selectedDeviceModelObject?.deviceModelId;
});
const deviceModelName = computed(() => {
  return store.state.offers.selectedDeviceModelObject?.modelSelect;
});

const disablePricing = ref(false);
const deviceRetailPriceInput = ref();
const deviceRetailPrice = ref();
const cartRetailPrice = computed(() => {
  return isCartAccessoryBundleEnabled.value && cartAccessoryPrice.value
    ? parseFloat(deviceRetailPrice.value) + parseFloat(cartAccessoryPrice.value)
    : parseFloat(deviceRetailPrice.value);
});
const deviceDownPayment = ref();

const displayFeesAndTaxes = computed(() => {
  return (
    store.state.carts.feeSchema &&
    displayPaymentTerms.value &&
    isColombiaSaleFlow()
  );
});

const feeSchema = computed(() => {
  return store.state.carts.feeSchema;
});
const showFeesAndTaxesV2 = ref(false);

const onboardingAwareNotLoading: ComputedRef<boolean> = computed(() => {
  return (
    (!loadingOffers.value && !isSelfOnboarding.value) ||
    (isSelfOnboarding.value && !loadingOffers.value && surveyIsComplete.value)
  );
});
const feesAndTaxesV2Enabled = computed(() => {
  return (
    feeSchema.value &&
    store.state.carts.months &&
    displayPaymentTerms.value &&
    (isPeruSaleFlow() || isColombiaSaleFlow())
  );
});

const loadingProgress = ref(0);

const setSelectedOffer = (value: number) => {
  store.dispatch("carts/setSelectedOffer", value);

  if (feesAndTaxesV2Enabled.value) {
    showFeesAndTaxesV2.value = true;
  }

  selectExperimentLog(value);
  selectedOffer.value = value;
  buttonContinue.value = true;
  scrollToBottom();
};

const retailPriceField = ref();
const downPaymentField = ref();
const disableDownPayment = computed(() => {
  if (store.state.merchant?.merchant?.country === "ZA") {
    return !store.state.carts?.priceWithSalesTax;
  }
  if (isSubscriptionFlow.value) {
    return true;
  }
  return false;
});

const useChatPopup = computed(() => {
  return (
    store.state.merchant?.merchant?.country === "ZA" &&
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.USE_ADA_CHATBOT_AUTO_TRIGGER
    )
  );
});

const checkChatConditions = () => {
  if (isSelfOnboarding.value) {
    openChatAfterSeconds(240, GreetingKey.SELF_ONBOARDING);
  } else if (useChatPopup.value) {
    openChatAfterSeconds(300);
  }
};

const checkCartMonths = () => {
  if (store.state.carts.months != null) {
    selectedOffer.value = store.state.carts.months;
  }
};

const checkWelcomeCallStatus = () => {
  getWelcomeCallStatus(store.state.carts.cartTag)
    .then((response: AxiosResponse<unknown>) => {
      const { welcomeCallRequired } = response.data as IWelcomeCall;

      store.dispatch("carts/setWelcomeCallRequired", welcomeCallRequired);
    })
    .catch((error) => {
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
    });
};

onMounted(() => {
  useIntervalCustomerState();
  useMerchantPersistState(() => true);
  useCartPersistState(() => {
    useUpdatePageState();
    checkChatConditions();
    autoFillPreApprovedOffer();
    checkCartMonths();
    logEvent(EventLogs.Events.OFFERS_SCREEN_LOADED);
    return true;
  });
});

const loadSelfOnboardingNextStep = async () => {
  if (isSelfOnboarding.value) {
    const callerPage = "OFFERS";
    await getNextRoute(store.state.carts.cartTag, callerPage)
      .then((response: AxiosResponse) => {
        const { nextRoute } = response.data;
        nextRouteIsSelfOnboarding.value = nextRoute;
      })
      .catch((error: AxiosError) => {
        errorMessage.value = getAPIErrorMessage(error.response?.data, t);
      });
  }
};
const forceRerender = () => {
  componentKeyModel.value += 1;
  componentKeyBrand.value += 1;
  componentKeyManufacturer.value += 1;
};
const computePromotionalDiscount = function (responseData, downPayment) {
  promotionalDiscount.value = convertToMoney(
    responseData.minDownPaymentUndiscounted - downPayment
  );
  isPromotionalDiscountApplied.value =
    responseData.isPromotionalDiscountApplied;

  if (
    promotionalDiscount.value > 0 &&
    downPayment < minDownPaymentUndiscounted.value
  ) {
    notifyPromotionalDiscountSelected.value = t(
      "PROMOTIONAL_DP_DISCOUNT_APPLIED_MESSAGE",
      {
        currency: currency.value.sign,
        amount: formatPrice(promotionalDiscount.value),
      }
    );
  } else {
    notifyPromotionalDiscountSelected.value = t(
      "PROMOTIONAL_DP_DISCOUNT_ELIGIBLE_MESSAGE",
      {
        currency: currency.value.sign,
        amount: formatPrice(maxDiscountEligible.value),
      }
    );
  }
};

const updateRetailPrice = (payload: {
  cartTag: string;
  retailPrice: number;
  accessoryPrice?: number | null | undefined;
  accessoryType?: ICartAccessoryType | null | undefined;
  accessoryIsRemoved?: boolean | null | undefined;
}) => {
  logPricingSetRetailPrice(payload.retailPrice);

  setPrice(
    payload.cartTag,
    payload.retailPrice,
    payload?.accessoryPrice,
    payload?.accessoryType,
    payload?.accessoryIsRemoved
  ).then(
    (response: AxiosResponse) => {
      store.dispatch("carts/setCart", response.data.cart);
      const responseData = response.data;

      minDownPaymentUndiscounted.value =
        responseData.minDownPaymentUndiscounted;

      if (promotionalDpExperiement.value) {
        maxDiscountEligible.value = convertToMoney(
          responseData.minDownPaymentUndiscounted - responseData.downPayment
        );
        computePromotionalDiscount(responseData, responseData.downPayment);
      }

      store.dispatch("offers/setDownPaymentRange", {
        downPayment: responseData.downPayment,
        maxDownPayment: responseData.maxDownPayment,
        minDownPayment: responseData.minDownPayment,
      });

      store.dispatch("carts/setDownPayment", response.data.downPayment);
      deviceDownPayment.value = response.data.downPayment.toString();
      downPaymentField.value?.componentReady(true);
      displayPaymentTerms.value = true;
      minDownPayment.value = responseData.minDownPayment;

      logEvent(EventLogs.Events.TERMS_LOADED);
      updatePricing();

      return;
    },
    (error: AxiosError) => {
      if (error) console.log("ERROR_SOMETHING_GOES_WRONG");
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
    }
  );
};

const updateDownPayment = (payload: {
  cartTag: string;
  downPayment: number;
}) => {
  pricingLoader.value = true;
  logPricingSetDownPayment(payload.downPayment);

  setDownPayment(payload.cartTag, payload.downPayment).then(
    (response: AxiosResponse) => {
      store.dispatch("carts/setCart", response.data.cart);
      if (deviceDownPayment.value !== response.data.cart.downPayment) {
        deviceDownPayment.value = response.data.cart.downPayment;
      }
      if (promotionalDpExperiement.value) {
        computePromotionalDiscount(response.data, deviceDownPayment.value);
      }
      logPricingValidPayment();
      updatePricing();
    },
    (error: AxiosError) => {
      if (error) console.log("ERROR_SOMETHING_GOES_WRONG");
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
    }
  );
};

const updatePricing = () => {
  pricingLoader.value = true;
  displayPaymentTerms.value = false;
  showFeesAndTaxesV2.value = false;

  const getPricingStart = performance.now();

  getPricing(cartTag.value)
    .then((response: AxiosResponse) => {
      store.dispatch("carts/setCart", response.data.cart);
      pricingLoader.value = false;
      displayPaymentTerms.value = true;
      logEvent(EventLogs.Events.TERMS_LOADED);
      store.dispatch("carts/updatePricing", response.data.pricing);
      logDownpaymentReductionMessage(
        minDownPayment.value,
        minDownPaymentUndiscounted.value,
        isPromotionalDiscountApplied.value,
        store
      );
      scrollToBottom();
    })
    .catch((error: AxiosError) => {
      pricingLoader.value = false;
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
    })
    .finally(() => {
      const getPricingLoadTime = performance.now() - getPricingStart;
      // Track get pricing api call load time in RUM.
      datadogRum.addTiming("m3-api-get-pricing");
      datadogRum.addAction("m3-api-get-pricing", {
        loadTime: getPricingLoadTime,
      });
    });
};

const setSelectedManufacturerDropdown = (value: IOptionDropdown) => {
  clearSelected();
  loadSelfOnboardingNextStep();
  setSelectedManufacturer(value);
};
const setSelectedManufacturer = (value: IOptionDropdown) => {
  clearMessages();
  buttonContinue.value = false;

  store.dispatch("offers/setSelectedManufacturer", value);
  store.dispatch("offers/setSelectedDeviceFamily", []);
  store.dispatch("offers/setSelectedDeviceFamilyModels", null);
  store.dispatch("offers/setSelectedDeviceModelObject", null);
  store.dispatch("offers/setSelectedDeviceModel", null);

  store.dispatch("offers/setSelectedImageUrl", null);
  store.dispatch("offers/setPricing", null);

  store.dispatch("carts/setRetailPrice", null);

  displayPaymentTerms.value = false;
  isPromotionalDiscountApplied.value = false;
  const manufacturer = value.name;
  const payload = {
    cartTag: store.state.carts.cartTag,
    manufacturer: manufacturer,
  };
  logPricingSetManufacturer(manufacturer);

  store.dispatch("offers/loadDeviceBrands", payload);
  forceRerender();
};

const setSelectedDeviceFamilyDropdown = (value: IOptionDropdown) => {
  clearSelected();
  setSelectedDeviceFamily(value);
};

const setSelectedDeviceFamily = (value: IOptionDropdown) => {
  clearMessages();
  buttonContinue.value = false;

  store.dispatch("offers/setSelectedDeviceFamily", value);

  store.dispatch("offers/setSelectedDeviceModelObject", null);
  store.dispatch("offers/setSelectedDeviceModel", null);

  store.dispatch("offers/setSelectedImageUrl", null);
  store.dispatch("offers/setPricing", null);
  store.dispatch("carts/setRetailPrice", null);
  displayPaymentTerms.value = false;
  isPromotionalDiscountApplied.value = false;
  const families = store.state.offers.deviceFamilies;

  logPricingSetFamily(value.name, value.code);
  Object.keys(families).forEach((family) => {
    if (family === store.state.offers.selectedDeviceFamily.code) {
      const selectedFamily = families[family];

      store.dispatch(
        "offers/setSelectedDeviceFamilyModels",
        selectedFamily.models
      );

      const models = selectedFamily.models;
      const parsedModels: IOptionDropdown[] = Object.keys(models).map((key) => {
        const displayName = models[key].description
          ? models[key].modelSelect + " ( " + models[key].description + " ) "
          : models[key].modelSelect;
        return {
          name: displayName,
          code: models[key].deviceModelId,
          description: models[key].description,
        };
      });
      store.dispatch("offers/loadDeviceModels", parsedModels);
    }
  });

  if (isSelfOnboarding.value) {
    selectDefaultDeviceModel();
  }

  forceRerender();
};

const setSelectedDeviceModelDropdown = (value: IOptionDropdown) => {
  clearSelected();
  setSelectedDeviceModel(value);
};

const setSelectedDeviceModel = async (value: IOptionDropdown) => {
  await store.dispatch("carts/setRetailPrice", null);
  await store.dispatch("offers/setSelectedDeviceModel", value);
  logPricingSetDevice(value.name, value.code);
  deviceDownPayment.value = "";
  buttonContinue.value = false;
  isPromotionalDiscountApplied.value = false;
  clearMessages();
  await store.dispatch(
    "offers/setSelectedImageUrl",
    useAsset("img/phone-preview.png")
  );
  getFinanceAmount(value);
};

const emitPrice = (value: number, onChange: number) => {
  logEvent(EventLogs.Events.PRICE_EDIT_ATTEMPTED);
  selectedOffer.value = 0;
  displayPaymentTerms.value = false;
  buttonContinue.value = false;
  isPromotionalDiscountApplied.value = false;
  const minRetailPrice =
    store.state.offers.selectedDeviceModelObject?.minRetailPrice || 0;
  deviceRetailPrice.value = value;

  if (cartRetailPrice.value < minRetailPrice) {
    logEvent(EventLogs.Events.OUT_OF_RANGE_PRICE_ERROR_DISPLAYED, {
      type: "low retail price",
    });
    errorMessage.value = `${t("LOW_RETAIL_PRICE")}`;
    disableOfferButton.value = true;
    retailPriceField.value?.componentReady(false);
    return;
  }

  const maxRetailPrice =
    store.state.offers.selectedDeviceModelObject?.maxRetailPrice || 0;

  if (cartRetailPrice.value > maxRetailPrice) {
    logEvent(EventLogs.Events.OUT_OF_RANGE_PRICE_ERROR_DISPLAYED, {
      type: "over max retail price",
    });
    errorMessage.value = `${t("MAX_RETAIL_PRICE")}`;
    disableOfferButton.value = true;
    retailPriceField.value?.componentReady(false);
    return;
  }

  errorMessage.value = "";
  disableOfferButton.value = false;
  retailPriceField.value?.componentReady(true);

  const payload = {
    cartTag: store.state.carts.cartTag,
    retailPrice: cartRetailPrice.value,
    accessoryPrice: null,
    accessoryType: null,
    accessoryIsRemoved: null,
  };

  if (isCartAccessoryBundleEnabled.value) {
    payload.accessoryPrice = cartAccessoryPrice.value;
    payload.accessoryType = cartAccessoryType.value;
    payload.accessoryIsRemoved = cartAccessoryIsRemoved.value;
  }

  if (payload && (onChange == 0 || !showButton.value)) {
    updateRetailPrice(payload);
  }
};

const emitDownPayment = (value: number, onChange: number) => {
  selectedOffer.value = 0;
  isPromotionalDiscountApplied.value = false;
  const minFinanceAmountIncrement =
    store.state.offers.selectedDeviceModelObject.minFinanceAmountIncrement ||
    0.01;
  const decimals = currency.value?.decimals ?? 2;

  if (decimals === 0) {
    value = Math.round(
      Math.floor(value / minFinanceAmountIncrement) * minFinanceAmountIncrement
    );
  } else {
    value = parseFloat(
      (
        Math.floor(value / minFinanceAmountIncrement) *
        minFinanceAmountIncrement
      ).toFixed(decimals)
    );
  }

  deviceDownPayment.value = String(value);
  displayPaymentTerms.value = false;
  buttonContinue.value = false;

  const minDownPayment =
    store.state.offers.selectedDeviceModelObject.minDownPayment || 0;
  if (value < minDownPayment) {
    logEvent(EventLogs.Events.OUT_OF_RANGE_DOWN_PAYMENT_ERROR_DISPLAYED, {
      type: "low down payment",
    });
    disableOfferButton.value = true;
    errorMessage.value = `${t("LOW_DOWN_PAYMENT")} ${formatCurrencyFromMerchant(
      minDownPayment
    )}`;
    disableOfferButton.value = true;
    retailPriceField.value?.componentReady(false);
    return;
  }

  const maxDownPayment =
    store.state.offers.selectedDeviceModelObject.maxDownPayment || 0;
  if (value > maxDownPayment) {
    logEvent(EventLogs.Events.OUT_OF_RANGE_DOWN_PAYMENT_ERROR_DISPLAYED, {
      type: "over max down payment",
    });
    disableOfferButton.value = true;
    errorMessage.value = `${t("MAX_DOWN_PAYMENT")} ${formatCurrencyFromMerchant(
      maxDownPayment
    )}`;
    disableOfferButton.value = true;
    retailPriceField.value?.componentReady(false);
    return;
  }

  errorMessage.value = "";
  disableOfferButton.value = false;
  downPaymentField.value?.componentReady(true);
  const payload = {
    cartTag: store.state.carts.cartTag,
    downPayment: value,
  };

  if (payload && (onChange == 0 || !showButton.value)) {
    updateDownPayment(payload);
  }
  scrollToBottom();
};

const router = useRouter();

const postSelectOffers = () => {
  checkWelcomeCallStatus();
  logPostSelectOffers(selectedOffer.value, store);
  putOffers(cartTag.value, store.state.carts.months).then(
    (response: AxiosResponse) => {
      const cart = response.data.cart;
      store.dispatch("carts/setCart", cart);
      logSendLatestCart(cart);
      if (isQuickApprovalFlow.value) {
        return router.push({ name: RouteName.M3PostOfferRegistration, params: { countryCode: getCountryCode() } });
      }

      if (isBrazilSaleFlow() && !isSubscriptionFlow.value) {
        createBankProposal(cartTag.value)
          .then(() => {
            logEvent(EventLogs.Events.BANK_PROPOSAL_CREATED);
            router.push({
              name: isSelfOnboarding.value
                ? nextRouteIsSelfOnboarding.value
                : RouteName.M3Imei,
              params: { countryCode: getCountryCode() },
            });
          })
          .catch((error) => {
            errorMessage.value = getAPIErrorMessage(error.response?.data, t);
          });
      } else if (isSelfOnboarding.value) {
        router.push({ name: nextRouteIsSelfOnboarding.value, params: { countryCode: getCountryCode() } });
      } else if (isBrazilSaleFlow()) {
        // subscription flow
        router.push({ name: RouteName.M3Imei, params: { countryCode: getCountryCode() } });
      } else if (isDownPaymentMerchantFlagEnabled()) {
        router.push({ name: RouteName.M3ElectronicDownPaymentStatus, params: { countryCode: getCountryCode() } });
      } else if (merchantHasUseAddOnServicesFlag()) {
        router.push({ name: RouteName.M3AddOns, params: { countryCode: getCountryCode() } });
      } else {
        router.push({ name: RouteName.M3Contract, params: { countryCode: getCountryCode() } });
      }
    },
    (error: AxiosError) => {
      if (error) {
        errorMessage.value = getAPIErrorMessage(error.response?.data, t);
      }
    }
  );
  localStorage.removeItem(jointModalShownKey);
};

const clearMessages = () => {
  errorMessage.value = "";
  store.dispatch("carts/setSelectedOffer", 0);
  selectedOffer.value = 0;
};

const clearSelected = () => {
  selectedDeviceModelId.value = -1;
  deviceRetailPriceInput.value = "";
  deviceDownPayment.value = "";
  displayPaymentTerms.value = false;
  showFeesAndTaxesV2.value = false;
  showPayjoyCardBanner.value = false;
  showJointLoanModal.value = false;
};

const isCheckoutOnSimultaneousLoanLine: ComputedRef<boolean> = computed(() =>
  store.getters["carts/hasActiveFeature"](
    CartActiveFeatures.CHECKOUT_ON_SIMULTANEOUS_LOAN_LINE
  )
);

const isJointLoanAndExistingLine: ComputedRef<boolean> = computed(() =>
  store.getters["carts/hasActiveFeature"](
    CartActiveFeatures.JOINT_LOAN_AND_EXISTING_LINE
  )
);

const getFinanceAmount = (deviceModel: IOptionDropdown) => {
  showPriceInputLoader.value = true;

  updateDeviceModel(
    cartTag.value,
    deviceModel.code,
    deviceModel?.description
  ).then(
    (response: AxiosResponse) => {
      showPriceInputLoader.value = false;
      const {
        maxRetailPrice,
        minRetailPrice,
        fixedPrice,
        minFinanceAmountIncrement,
      } = response.data;
      const deviceModelId = response.data.cart.deviceModelId;
      disablePricing.value = fixedPrice;
      if (isCheckoutOnSimultaneousLoanLine.value) {
        recordCheckoutOnSimultaneousLoanLine();

        const clLimit = response.data?.creditLineLimit ?? 0;
        if (clLimit && clLimit !== 0) {
          logEvent(EventLogs.Events.PAYJOY_CARD_APPROVED_BANNER_SHOWN, {
            creditLineInitialLimit: clLimit,
          });
        } else {
          logEvent(EventLogs.Events.PAYJOY_CARD_DISAPPROVED_BANNER_SHOWN, {
            creditLineInitialLimit: clLimit,
          });
        }
        showPayjoyCardBanner.value = true;
        creditLineLimit.value = clLimit;
      }

      if (
        isJointLoanAndExistingLine.value &&
        !localStorage.getItem(jointModalShownKey)
      ) {
        showJointLoanModal.value = true;
        localStorage.setItem(jointModalShownKey, "true");
      }

      store.dispatch("offers/setSelectedDeviceModelObject", {
        deviceModelId,
      });

      store.dispatch("offers/setRetailPriceRange", {
        minRetailPrice: minRetailPrice,
        maxRetailPrice: maxRetailPrice,
        minFinanceAmountIncrement: minFinanceAmountIncrement,
      });

      store.dispatch("carts/setCart", response.data.cart);
      // if useDefaultPricing is set, then we set
      // the price of the device to retailPrice
      if (disablePricing.value) {
        deviceRetailPriceInput.value = convertToMoney(
          response.data.retailPrice
        );
        const payload = {
          cartTag: cartTag.value,
          retailPrice: response.data.retailPrice,
          defaultPricing: disablePricing.value,
          accessoryPrice: null,
          accessoryType: null,
          accessoryIsRemoved: null,
        };

        if (isCartAccessoryBundleEnabled.value) {
          payload.accessoryPrice = cartAccessoryPrice.value;
          payload.accessoryType = cartAccessoryType.value;
          payload.accessoryIsRemoved = cartAccessoryIsRemoved.value;
        }

        updateRetailPrice(payload);
      } else {
        retailPriceField.value?.componentReady(true);
      }
      selectedOffer.value = 0;
      scrollToBottom();
      deviceModelFinanceLoaded.value = true;
    },
    (error: AxiosError) => {
      errorMessage.value = getAPIErrorMessage(error.response?.data, t);
      if (error) console.log("ERROR_SOMETHING_GOES_WRONG");
    }
  );
};

const recordCheckoutOnSimultaneousLoanLine = () => {
  recorder(app)?.recordEvent(EVENTS.CHECKOUT_ON_SIMULTANEOUS_LOAN_LINE);
};

const convertToMoney = (value) => {
  const decimals = currency.value?.decimals ?? 2;
  if (decimals === 0) {
    return Math.round(parseFloat(value));
  }
  return parseFloat(parseFloat(value).toFixed(decimals));
};

const isSelfOnboarding = computed(() => {
  return store.state.carts?.productVersion == ProductVersion.M3_SELF_ONBOARDING;
});

const shouldShowWarningAlert = computed(() => {
  return (
    showWarningAlert.value &&
    !isSelfOnboarding.value &&
    !isSubscriptionFlow.value
  ) || (showWarningAlert.value && isRentalFlow.value);
});

const selectDefaultDeviceModel = () => {
  const deviceModels = store.state.offers?.deviceModelsDropdown;
  if (deviceModels.length > 0) {
    setSelectedDeviceModel(deviceModels[0]);
  }
};

const emitRecommendedOffer = (selectedRecommendedOffer) => {
  store.state.offers.deviceFamilies = [];
  selectedDeviceModelId.value = selectedRecommendedOffer.selectedRow;
  // Set Manufacturer
  const selectedManufacturer = selectedRecommendedOffer.manufacturer;
  const selectedModelId = selectedRecommendedOffer.deviceModelId.toString();
  const selectedPrice = selectedRecommendedOffer.retailPrice.toString();
  const selectedDownpayment =
    selectedRecommendedOffer.minDownPayment.toString();
  const description = selectedRecommendedOffer.description;

  let selectedManufacturerCode = "";
  manufacturerDropdown.value.forEach((manufacturer) => {
    if (manufacturer.name === selectedManufacturer) {
      selectedManufacturerCode = manufacturer.code;
    }
  });

  const selectedRecommendedManufacturer = {
    name: selectedManufacturer,
    code: selectedManufacturerCode,
  };
  selectedManufacturerM.value = selectedRecommendedManufacturer;

  setSelectedManufacturer(selectedRecommendedManufacturer);

  // Set Device Family
  let timer = 0;
  const interval = setInterval(() => {
    timer++;
    const families = store.state.offers.deviceFamilies;
    if (families.length) {
      clearInterval(interval);

      let familyName = "";
      let familyCode = "";
      let modelName = "";
      families.forEach((family, indexFamily) => {
        const models = family.models;
        models.forEach((model) => {
          if (model.deviceModelId === selectedModelId) {
            familyName = family.deviceFamilyName;
            familyCode = indexFamily.toString();
            modelName = model.modelSelect;
          }
        });
      });
      const selectedRecommendedDevice = {
        name: familyName,
        code: familyCode,
      };
      setSelectedDeviceFamily(selectedRecommendedDevice);

      // Set Device Model
      const selectedRecommendedModel = {
        name: modelName,
        code: selectedModelId,
        description: description,
      };
      setSelectedDeviceModel(selectedRecommendedModel);
      displayPaymentTerms.value = false;

      timer = 0;
      const intervalModel = setInterval(() => {
        timer++;
        if (deviceModelFinanceLoaded.value) {
          clearInterval(intervalModel);

          // Set Price and Downpayment
          deviceRetailPriceInput.value = selectedPrice;
          deviceDownPayment.value = String(selectedDownpayment);

          const intervalDownpayment = setInterval(() => {
            timer++;
            if (
              retailPriceField.value?.inputValue &&
              downPaymentField.value?.inputValue
            ) {
              clearInterval(intervalDownpayment);

              const flags = store.state.merchant?.merchantFlags;
              if (!flags.includes(MerchantFlags.USE_DEFAULT_PRICING)) {
                emitPrice(selectedPrice, 0);
              }
            }
            if (timer > 7) {
              clearInterval(intervalDownpayment);
            }
          }, 1000);
        }

        if (timer > 7) {
          clearInterval(intervalModel);
        }
      }, 1000);
    }
    if (timer > 7) {
      clearInterval(interval);
    }
  }, 1000);
};

const dropdownLabelClass = computed(() => {
  return isSelfOnboarding.value ? "self-onboarding-label-font-size" : "";
});

const experiment = computed(() => {
  const experiments = store.state.carts?.experiments;
  if (experiments?.lowerWeeklyPaymentBadges) {
    return IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES;
  } else if (experiments?.monthTermPopularityBadges) {
    return IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES;
  } else if (experiments?.lowestTotalPaymentBadge) {
    return IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE;
  } else {
    return IBadgeExperiments.NO_PAYMENT_BADGE;
  }
});

const getActionLogEvent = computed(() => {
  switch (experiment.value) {
    case IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES:
      return ActionLogs.Events.TERM_OPTIONS_LOWER_WEEKLY_PAYMENT_BADGES_CLICKED;
    case IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES:
      return ActionLogs.Events
        .TERM_OPTIONS_MONTH_TERM_POPULARITY_BADGES_CLICKED;
    case IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE:
      return ActionLogs.Events.TERM_OPTIONS_LOWEST_TOTAL_PAYMENT_BADGE_CLICKED;
    default:
      return ActionLogs.Events.TERM_OPTIONS_NO_PAYMENT_BADGE;
  }
});

const selectExperimentLog = function (index: number) {
  switch (experiment.value) {
    case IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES:
      logMonthTermPopularityBadges(index);
      break;
    case IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE:
      logLowestTotalPaymentBadge(index);
      break;
    default:
      experimentLog(index);
  }
};

const getActionEventName = computed(() => {
  switch (experiment.value) {
    case IBadgeExperiments.LOWER_WEEKLY_PAYMENT_BADGES:
    case IBadgeExperiments.MONTH_TERM_POPULARITY_BADGES:
    case IBadgeExperiments.LOWEST_TOTAL_PAYMENT_BADGE:
      return "offersClicked_v3badges";
    default:
      return "offersTermsClicked";
  }
});
const logMonthTermPopularityBadges = function (index: number) {
  let paymentTerms: IPaymentTerms = {} as IPaymentTerms;
  const maxPopularityValue = getMaxPopularityValue();
  // Iterate over the available offers
  Object.keys(availableOffers.value).forEach((key) => {
    const offer = availableOffers.value[key];
    const popularity = offer.popularity;
    const popularityValue = popularity
      ? parseInt(popularity.replace("+", ""))
      : null;
    // Set initial values for the offer
    paymentTerms[key] = {
      popularity: popularity,
      downPayment: offer.downPayment,
      monthlyPrice: offer.monthlyPayment,
      weeklyPrice: offer.weeklyPayment,
      months: offer.months,
      weeks: offer.weeks,
      best: popularityValue === maxPopularityValue ? 1 : 0,
      clicked: parseInt(key) == index ? 1 : 0,
    };
  });
  // Log the action with the calculated payment terms
  logAction(getActionLogEvent.value, {
    eventName: getActionEventName.value,
    deviceModelId: deviceModelId.value,
    devicePrice: store.state.carts?.priceWithSalesTax,
    minDownPayment:
      store.state.offers.selectedDeviceModelObject?.minDownPayment,
    termsOptions: paymentTerms,
  });
};
const getMaxPopularityValue = function () {
  const result: number[] = [];
  for (const [, value] of Object.entries(availableOffers.value)) {
    const offerList = JSON.parse(JSON.stringify(value));
    if (offerList.popularity) {
      offerList.popularity.replace("+", "");
      result.push(parseInt(offerList.popularity));
    }
  }
  return Math.max(...result);
};
const logLowestTotalPaymentBadge = function (index: number) {
  let paymentTerms: IPaymentTerms = {} as IPaymentTerms;
  const lowestKey = Math.min(
    ...Object.keys(availableOffers.value).map((key) => Number(key))
  );
  Object.keys(availableOffers.value).map((key) => {
    paymentTerms[key] = {
      downPayment: availableOffers.value[key].downPayment,
      monthlyPrice: availableOffers.value[key].monthlyPayment,
      weeklyPrice: availableOffers.value[key].weeklyPayment,
      months: availableOffers.value[key].months,
      weeks: availableOffers.value[key].weeks,
      best: lowestKey == parseInt(key) ? 1 : 0,
      clicked: parseInt(key) == index ? 1 : 0,
    };
  });
  logAction(getActionLogEvent.value, {
    eventName: getActionEventName.value,
    deviceModelId: deviceModelId.value,
    devicePrice: store.state.carts?.priceWithSalesTax,
    minDownPayment:
      store.state.offers.selectedDeviceModelObject?.minDownPayment,
    termsOptions: paymentTerms,
  });
};

const experimentLog = function (index: number) {
  let paymentTerms: IPaymentTerms = {} as IPaymentTerms;
  let lowestPayment = Math.min(
    ...Object.keys(availableOffers.value).map((key) => {
      return availableOffers.value[key].weeklyPayment;
    })
  );
  let maxPayment = Math.max(
    ...Object.keys(availableOffers.value).map((key) => {
      return availableOffers.value[key].weeklyPayment;
    })
  );
  Object.keys(availableOffers.value).map((key) => {
    let bestTerm: number | null;
    if (availableOffers.value[key].weeklyPayment === maxPayment) {
      bestTerm = null;
    } else {
      bestTerm =
        availableOffers.value[key].weeklyPayment === lowestPayment ? 1 : 0;
    }
    paymentTerms[key] = {
      downPayment: availableOffers.value[key].downPayment,
      monthlyPrice: availableOffers.value[key].monthlyPayment,
      weeklyPrice: availableOffers.value[key].weeklyPayment,
      months: availableOffers.value[key].months,
      weeks: availableOffers.value[key].weeks,
      best: bestTerm,
      clicked: parseInt(key) == index ? 1 : 0,
    };
  });
  logAction(getActionLogEvent.value, {
    eventName: getActionEventName.value,
    deviceModelId: deviceModelId.value,
    devicePrice: store.state.carts?.priceWithSalesTax,
    minDownPayment:
      store.state.offers.selectedDeviceModelObject?.minDownPayment,
    termsOptions: paymentTerms,
  });
};
const obrLocalStorage = {
  deviceModelId: localStorage.getItem("pre_reg_device_model_id"),
  selectedManufacturer: localStorage.getItem("pre_reg_manufacturer"),
  retailPrice: localStorage.getItem("pre_reg_retail_price"),
  minDeposite: localStorage.getItem("pre_reg_min_deposite"),
};

const offersPreRegistrationExperiment = computed(() => {
  return store.state.carts?.experiments?.offersPreRegistration
    ? store.state.carts?.experiments?.offersPreRegistration
    : false;
});

const autoFillPreApprovedOffer = () => {
  store.state.offers.deviceFamilies = [];
  const deviceModelId = obrLocalStorage.deviceModelId;
  const selectedManufacturer = obrLocalStorage.selectedManufacturer;
  const retailPrice = obrLocalStorage.retailPrice;
  const minDeposite = obrLocalStorage.minDeposite;

  if (
    deviceModelId != null &&
    selectedManufacturer != null &&
    offersPreRegistrationExperiment.value
  ) {
    let selectedManufacturerCode = "";
    manufacturerDropdown.value.forEach((manufacturer) => {
      if (manufacturer.name === selectedManufacturer) {
        selectedManufacturerCode = manufacturer.code;
      }
    });

    const selectedRecommendedManufacturer = {
      name: selectedManufacturer,
      code: selectedManufacturerCode,
    };
    selectedManufacturerM.value = selectedRecommendedManufacturer;

    setSelectedManufacturer(selectedRecommendedManufacturer);

    // Set Device Family
    let timer = 0;
    const interval = setInterval(() => {
      timer++;
      const families = store.state.offers.deviceFamilies;
      if (families.length) {
        clearInterval(interval);

        families.forEach((family) => {
          const models = family.models;
          models.forEach((model) => {
            if (parseInt(model.deviceModelId) === parseInt(deviceModelId)) {
              modelIdMatched.value = true;
              preApprovalState.value = ApprovalState.APPROVED;
              const offerSelected = {
                manufacturer: selectedManufacturer,
                deviceModelId: deviceModelId,
                retailPrice: retailPrice,
                minDownPayment: minDeposite,
                description: "",
                selectedRow: deviceModelId,
              };
              emitRecommendedOffer(offerSelected);
              clearInterval(interval);
            }
          });
        });
        if (!modelIdMatched.value) {
          preApprovalState.value = ApprovalState.DISAPPROVED;
        }
      }
      if (timer > 2) {
        clearInterval(interval);
      }
    }, 1000);
  }
};

const loadingInterval = ref();

const startLoadingAnimation = () => {
  const totalTime = 6 * 60 * 1000; // Offers API can take up to 6 min to answer
  loadingProgress.value = 0;
  loadingInterval.value = setInterval(() => {
    loadingProgress.value++;
    if (loadingProgress.value >= 100) {
      clearInterval(loadingInterval.value);
    }
  }, totalTime / 100);
};

watch(
  idAnnotations,
  (newValue, oldValue) => {
    if (newValue !== oldValue && store.state.carts.cartTag) {
      store.dispatch("offers/loadManufacturer", store.state.carts.cartTag);
      startLoadingAnimation();
    }
  },
  { immediate: true }
);

watch(
  () => store.state.offers?.deviceFamiliesDropdown,
  (newValue) => {
    if (newValue && recommendedDevice.value && isSelfOnboarding.value) {
      setSelectedDeviceFamily({
        code: "0",
        name: recommendedDevice.value.family,
      });
    }
  },
  { deep: true }
);

const showPreOffers = ref(true);
const showSurvey = ref();
const surveyIsComplete = ref(false);
const handleShowSurvey = (value: boolean) => {
  if (!value) {
    surveyIsComplete.value = true;
    logEvent(EventLogs.Events.SELF_SERVICE_SURVEY_SKIPPED);
    logAction(ActionLogs.Events.SELF_SERVICE_SURVEY_SKIP);
  }
  showSurvey.value = value;
  showPreOffers.value = false;
};
const completeSurvey = () => {
  showSurvey.value = false;
  surveyIsComplete.value = true;
};

const recommendedDevice = ref();
const surveyDeviceChosen = (device) => {
  completeSurvey();
  recommendedDevice.value = device;
  setSelectedManufacturerDropdown({
    name: recommendedDevice.value.manufacturer,
    code: recommendedDevice.value.manufacturer,
  });
};

const isDownPaymentMerchantFlagEnabled = () => {
  return (
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.PAY_AT_DOWN_PAYMENT
    ) ||
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.OPENPAY_DOWN_PAYMENT
    ) ||
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.PESPAY_DOWN_PAYMENT
    ) ||
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.ADMIN_API_DOWN_PAYMENT
    )
  );
};

onBeforeUnmount(() => {
  clearInterval(loadingInterval.value);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
h3 {
  color: $pj-color-zomp;
}
.list-move, /* apply transition to moving elements */
.list-leave-active {
  transition: all 0.3s linear;
}

.list-enter-active {
  transition: all 0.8s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}
.alert-info {
  font-weight: bold;
  font-size: 14px;
  padding: 0 10px;
  background-color: $pj-color-lighter-gray;
  color: black;
}
.alert-info1 {
  font-weight: bold;
  font-size: 14px;
  padding: 0 10px;
  background-color: #f3f8f6;
  color: black;
}
.approved-div {
  background-color: #eafaf3;
  border: 1px solid $pj-color-green;
  padding: 10px;
  margin: 10px 0;
  border-radius: 5px;

  h3 {
    font-size: 16px;
    color: $pj-color-green;
    margin: 0;
  }
}

.icon {
  font-weight: 100;
}
.down-payment-special-promotion-eligible {
  color: $pj-color-green;
}
</style>
